import { useState, useEffect, useCallback, useRef }  from 'react';
import { useNavigate }                              from 'react-router-dom';
import dayjs                                        from 'dayjs';
import customParseFormat                            from 'dayjs/plugin/customParseFormat';

import DateInput                                    from '../components/DateInput';
import FeedbackModal                                from '../components/Modals/FeedbackModal';
import ExportButtons                                from '../components/Buttons/ExportButtons';
import TrainQueueTable, { TrainQueueLegend, countCollapsedRows } from '../components/Table/TrainQueueTable';
import api                                          from '../services/api';
import { useUser }                                  from '../contexts/UserContext';
import { findPermission }                           from '../utils/UserPermissionUtils.js';
import '../styles/main.css';

dayjs.extend(customParseFormat);

const ACCESS_PERMISSION = 'Acesso à Pagina de Slots Baixada';
const EDIT_PERMISSION   = 'Edição Slots Baixada';

// O backend trabalha com AAAA-MM-DD; o DateInput do projeto, com DD/MM/YY.
const toInputFormat = (date) => dayjs(date, 'YYYY-MM-DD').format('DD/MM/YY');
const toBR          = (date) => dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');

const _backendError = (error) => error.response?.data?.error || error.message;

function TrainQueuePage() {

    const { userData } = useUser();
    const navigate     = useNavigate();

    const canEdit = !!findPermission(userData, EDIT_PERMISSION);

    const [ _dates,        _setDates        ] = useState([]);
    const [ _selectedDate, _setSelectedDate ] = useState(null);
    const [ _queue,        _setQueue        ] = useState(null);
    const [ _terminals,    _setTerminals    ] = useState([]);
    const [ _isLoading,    _setLoading      ] = useState(true);
    const [ _isCreating,   _setCreating     ] = useState(false);
    const [ _feedback,     _setFeedback     ] = useState(null);
    const [ _isCollapsed,  _setCollapsed    ] = useState(false);

    const _creatingRef = useRef(false);
    const _exportRef   = useRef(null);

    useEffect(() => {

        if (!userData?.permissions) return;

        if (!findPermission(userData, ACCESS_PERMISSION)) navigate('/home');

    }, [userData, navigate]);

    // Carrega as datas com fila; mantém a selecionada se ela ainda existir.
    const _fetchDates = useCallback(async (dateToSelect) => {

        try {

            const response = await api.get('/trainQueueDates');
            const dates    = response.data;

            _setDates(dates);
            _setSelectedDate(current => {
                const wanted = dateToSelect ?? current;
                return dates.includes(wanted) ? wanted : (dates[0] ?? null);
            });

            if (dates.length === 0) _setLoading(false);

        } catch (error) {
            _setLoading(false);
            _setFeedback({ type: 'error', title: 'Erro ao carregar os Slots Baixada', message: _backendError(error) });
        }

    }, []);

    useEffect(() => { _fetchDates(); }, [_fetchDates]);

    useEffect(() => {

        if (!_selectedDate) { _setQueue(null); return; }

        let cancelled = false;

        const fetchQueue = async () => {

            _setLoading(true);

            try {
                const response = await api.get(`/trainQueue/${_selectedDate}`);
                if (!cancelled) _setQueue(response.data);
            } catch (error) {
                if (!cancelled) _setFeedback({ type: 'error', title: 'Erro ao carregar a fila', message: _backendError(error) });
            } finally {
                if (!cancelled) _setLoading(false);
            }
        };

        fetchQueue();

        return () => { cancelled = true; };

    }, [_selectedDate]);

    // Terminais só são necessários para editar a coluna Terminal.
    useEffect(() => {

        if (!canEdit) return;

        api.get('/getAllTerminals')
            .then(response => {
                const terminals = [...(response.data.terminals || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
                _setTerminals(terminals.map(terminal => ({ value: terminal.id, label: terminal.name })));
            })
            .catch(error => console.error('Erro ao carregar terminais:', error));

    }, [canEdit]);

    const _handleCreateQueue = async () => {

        // Trava contra duplo clique (a ref muda na hora, antes do re-render).
        if (_creatingRef.current) return;
        _creatingRef.current = true;
        _setCreating(true);

        const today = dayjs().format('YYYY-MM-DD');

        try {

            const response = await api.post('/trainQueue', { date: today });
            const { copiedFrom } = response.data;

            _setFeedback({
                type:    'success',
                title:   'Fila criada',
                message: copiedFrom
                    ? `Fila de ${toBR(today)} criada a partir da fila de ${toBR(copiedFrom)}.`
                    : `Fila de ${toBR(today)} criada.`
            });

            await _fetchDates(today);

        } catch (error) {

            _setFeedback({ type: 'error', title: 'Erro ao atualizar a fila', message: _backendError(error) });

            // Se a fila de hoje já existia, mostra ela.
            if (error.response?.status === 409) await _fetchDates(today);

        } finally {

            _creatingRef.current = false;
            _setCreating(false);
        }
    };

    const _handleSaveCell = async (rowId, field, value) => {

        try {

            const response = await api.put(`/trainQueueRow/${rowId}`, { field, value });

            _setQueue(current => ({
                ...current,
                rows: current.rows.map(row => row.id === rowId ? response.data : row)
            }));

        } catch (error) {

            _setFeedback({ type: 'error', title: 'Erro ao salvar a célula', message: _backendError(error) });
        }
    };

    return (

        <div className="pg-content flex flex-col gap-[1.25rem]">

            <h1>Slots Baixada</h1>

            <section className="w-full bg-neutro borda-cinza rounded-[7px] p-[1rem] flex justify-between items-center gap-[10px]">

                <DateInput
                    selectedDateType="day"
                    datesList={_dates.map(toInputFormat)}
                    defaultValue={_selectedDate ? toInputFormat(_selectedDate) : null}
                    onChange={(newValue) => { if (newValue?.isValid?.()) _setSelectedDate(newValue.format('YYYY-MM-DD')) }}
                />

                {_queue && (
                    <button
                        type="button"
                        onClick={() => _setCollapsed(current => !current)}
                        title={_isCollapsed ? 'Mostrar todas as linhas' : `Recolher ${countCollapsedRows(_queue.rows)} linhas vazias`}
                        className={`flex items-center gap-[6px] h-10 px-4 rounded-[4px] text-[0.8rem] font-semibold border transition-colors ${_isCollapsed ? 'bg-azul-profundo-m text-white border-azul-profundo-m hover:bg-azul-profundo-e' : 'bg-white text-azul-profundo-m border-azul-profundo-m hover:bg-azul-ceu-c'}`}
                    >
                        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {_isCollapsed ? <path d="M7 15l5 5 5-5M7 9l5-5 5 5" /> : <path d="M7 20l5-5 5 5M7 4l5 5 5-5" />}
                        </svg>
                        {_isCollapsed ? 'Mostrar todas as linhas' : 'Recolher linhas vazias'}
                    </button>
                )}

                {canEdit && _queue && (
                    <div className="ml-auto">
                        <ExportButtons
                            getTarget={() => _exportRef.current}
                            fileName={`slots-baixada-${_selectedDate}`}
                            title={`Slots Baixada - ${toBR(_selectedDate)}`}
                            onError={(message) => _setFeedback({ type: 'error', title: 'Erro ao exportar a tabela', message })}
                        />
                    </div>
                )}

                {canEdit && (
                    <button
                        className={`flex ${_queue ? '' : 'ml-auto'} btn bg-azul-profundo-m hover:bg-azul-profundo-e h-10 items-center disabled:opacity-60 disabled:cursor-wait`}
                        onClick={_handleCreateQueue}
                        disabled={_isCreating}
                    >
                        {_isCreating ? 'Atualizando...' : 'Atualizar fila'}
                    </button>
                )}

            </section>

            {_isLoading && <p className="text-[0.9rem] text-cinza-escuro">Carregando...</p>}

            {!_isLoading && !_queue && (
                <p className="text-[0.9rem] text-cinza-escuro">
                    Nenhuma fila criada ainda.{canEdit && ' Clique em "Atualizar fila" para criar a fila de hoje.'}
                </p>
            )}

            {!_isLoading && _queue && (

                <section ref={_exportRef} className="flex flex-col xl:flex-row gap-4 items-start bg-[#f9f9f9] p-1">

                    <div className="flex-grow w-full min-w-0">
                        <TrainQueueTable
                            rows={_queue.rows}
                            isCollapsed={_isCollapsed}
                            terminalOptions={_terminals}
                            canEdit={canEdit}
                            onSaveCell={_handleSaveCell}
                        />
                    </div>

                    <TrainQueueLegend />

                </section>
            )}

            <FeedbackModal feedback={_feedback} onClose={() => _setFeedback(null)} />

        </div>
    );
}

export default TrainQueuePage;
